// Первый экран главной — «карта с пинами» (ТЗ владельца 25.10.2026): светлая
// карта (улицы и кварталы еле заметно), пять пинов-капель, разбросанных
// свободно, с наклоном ±3–11° и разного размера, между ними пунктир «Bir yol».
// Макет — канвас «Yolutap — главная с пинами» (Main 1440, Mobile 390).
//
// Карта — рисунок (SVG), не Google Maps: первый экран без скриптов карты и
// без сети. Пины — обычные ссылки на существующие страницы, «Dərsliklər» —
// кнопка, открывающая окно листа ожидания (textbook-waitlist.functions.ts).
//
// Координаты пинов — в единицах сцены (desk 1200×560, mob 360×600); сцена
// держит пропорции (aspect-ratio), поэтому пунктир в том же viewBox всегда
// приходит к пинам. Телефон (≤ 980px) — своя раскладка: «Karyera» крупно
// сверху, остальные меньше и вразнобой, все пять на первом экране.
//
// Тексты — pins-copy.ts. Проверка — scripts/verify-home-pins.mts.
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { animate, stagger } from "motion";

import { ev } from "./home-events";
import type { HomeLang } from "./HomePage";
import { PINS_COPY, WAITLIST_PHONE_RE, abroadPinLine, type PinId } from "./pins-copy";
import { doorTestHref } from "./HomeDoors";
import { STUDY_ABROAD_HREF, TUTORS_JOIN_HREF, TUTORS_MAP_HREF } from "@/components/site/SiteChrome";
import { normalizePhoneAz } from "@/lib/phone-az";
import { readUtm } from "@/lib/utm";

/** Куда ведёт пин — только существующие страницы. */
export function pinHref(pin: Exclude<PinId, "textbooks">, lang: HomeLang): string {
  if (pin === "career") return doorTestHref(lang);
  if (pin === "tutors") return TUTORS_MAP_HREF[lang];
  if (pin === "abroad") return STUDY_ABROAD_HREF[lang];
  return TUTORS_JOIN_HREF[lang];
}

type Place = { x: number; y: number; size: number; rot: number };
type PinSpec = { id: PinId; color: string; ink: string; tag: "fill" | "soft" | "amber"; icon: ReactNode; desk: Place; mob: Place; w: number; mw: number };

const ICON = {
  compass: (
    <>
      <circle cx="24" cy="23" r="7" />
      <path d="M27.5 19.5l-2.2 5.3-5.3 2.2 2.2-5.3z" />
    </>
  ),
  search: (
    <>
      <circle cx="22.5" cy="21.5" r="5" />
      <path d="M26.2 25.2l3.3 3.3" />
    </>
  ),
  globe: (
    <>
      <circle cx="24" cy="23" r="7" />
      <ellipse cx="24" cy="23" rx="3" ry="7" />
      <path d="M17 23h14" />
    </>
  ),
  book: (
    <>
      <path d="M17 18h5.5c.8 0 1.5.7 1.5 1.5V29c0-.8-.7-1.5-1.5-1.5H17z" />
      <path d="M31 18h-5.5c-.8 0-1.5.7-1.5 1.5V29c0-.8.7-1.5 1.5-1.5H31z" />
    </>
  ),
  plus: <path d="M24 17v12M18 23h12" />,
};

// x — центр пина, y — верх капли, size — высота капли (px), rot — наклон.
export const PINS: PinSpec[] = [
  { id: "abroad", color: "var(--color-purple)", ink: "var(--color-purple-deep)", tag: "soft", icon: ICON.globe, desk: { x: 215, y: 40, size: 112, rot: -7 }, mob: { x: 92, y: 206, size: 66, rot: -7 }, w: 250, mw: 168 },
  { id: "career", color: "var(--color-teal)", ink: "var(--color-teal)", tag: "fill", icon: ICON.compass, desk: { x: 600, y: 64, size: 156, rot: 3 }, mob: { x: 180, y: 0, size: 92, rot: 3 }, w: 290, mw: 250 },
  { id: "tutors", color: "var(--color-green-deep)", ink: "var(--color-green-deep)", tag: "soft", icon: ICON.search, desk: { x: 985, y: 20, size: 112, rot: 9 }, mob: { x: 270, y: 232, size: 66, rot: 9 }, w: 250, mw: 168 },
  { id: "textbooks", color: "var(--color-amber)", ink: "var(--color-amber-text)", tag: "amber", icon: ICON.book, desk: { x: 372, y: 318, size: 100, rot: -3 }, mob: { x: 96, y: 412, size: 60, rot: -3 }, w: 230, mw: 168 },
  { id: "tutor_join", color: "var(--color-ink-2)", ink: "var(--color-ink-2)", tag: "soft", icon: ICON.plus, desk: { x: 1065, y: 352, size: 80, rot: -11 }, mob: { x: 276, y: 440, size: 52, rot: -11 }, w: 230, mw: 160 },
];

const DESK = { w: 1200, h: 560 };
const MOB = { w: 360, h: 600 };

/** Тропинки «Bir yol»: от карьеры к остальным, от репетиторов к регистрации. */
const TRAILS: [PinId, PinId][] = [
  ["career", "abroad"],
  ["career", "tutors"],
  ["career", "textbooks"],
  ["tutors", "tutor_join"],
];

function trailPath(a: Place, b: Place): string {
  // от середины одной капли к середине другой, мягкой дугой
  const ax = a.x, ay = a.y + a.size * 0.42;
  const bx = b.x, by = b.y + b.size * 0.42;
  const mx = (ax + bx) / 2, my = (ay + by) / 2;
  const dx = bx - ax, dy = by - ay;
  const len = Math.hypot(dx, dy) || 1;
  const bend = Math.min(60, len * 0.18);
  const cx = mx - (dy / len) * bend, cy = my + (dx / len) * bend;
  return `M${ax.toFixed(1)} ${ay.toFixed(1)} Q${cx.toFixed(1)} ${cy.toFixed(1)} ${bx.toFixed(1)} ${by.toFixed(1)}`;
}

function Trails({ view, at, className }: { view: { w: number; h: number }; at: (p: PinSpec) => Place; className: string }) {
  const by = (id: PinId) => at(PINS.find((p) => p.id === id)!);
  return (
    <svg className={className} viewBox={`0 0 ${view.w} ${view.h}`} aria-hidden="true" focusable="false">
      {TRAILS.map(([a, b]) => (
        <path key={a + b} d={trailPath(by(a), by(b))} />
      ))}
    </svg>
  );
}

/** Светлая карта: кварталы, улицы, полоса воды — только украшение. */
function Streets() {
  return (
    <svg className="pp-streets" viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false">
      <g className="pp-streets__blocks">
        <rect x="40" y="560" width="150" height="110" rx="10" />
        <rect x="430" y="250" width="70" height="120" rx="10" />
        <rect x="760" y="620" width="170" height="90" rx="10" />
        <rect x="1250" y="300" width="150" height="130" rx="10" />
        <rect x="600" y="760" width="130" height="100" rx="10" />
        <rect x="980" y="480" width="110" height="80" rx="10" />
        <rect x="160" y="790" width="140" height="80" rx="10" />
        <rect x="1180" y="90" width="120" height="90" rx="10" />
        <rect x="120" y="120" width="130" height="80" rx="10" />
      </g>
      <path className="pp-streets__water" d="M0 870 C 300 830, 520 900, 820 850 S 1250 810, 1440 860 L1440 900 L0 900 Z" />
      <g className="pp-streets__main">
        <path d="M-40 520 C 230 470, 470 600, 790 560 S 1180 410, 1500 470" />
        <path d="M380 -20 C 410 420, 300 640, 420 940" />
        <path d="M1130 -20 C 1060 440, 1190 660, 1090 940" />
      </g>
      <g className="pp-streets__side">
        <path d="M-40 390 C 280 420, 640 330, 930 410 S 1300 470, 1500 380" />
        <path d="M760 -20 C 800 470, 690 650, 780 940" />
        <path d="M-40 740 C 380 710, 900 780, 1500 700" />
        <path d="M-40 200 C 400 230, 900 160, 1500 220" />
      </g>
    </svg>
  );
}

function Drop({ spec }: { spec: PinSpec }) {
  return (
    <span className="pp-drop" aria-hidden="true">
      <svg viewBox="0 0 48 60" focusable="false">
        <path fill={spec.color} d="M24 2C12.4 2 3 11.2 3 22.6 3 37 24 58 24 58s21-21 21-35.4C45 11.2 35.6 2 24 2z" />
        <circle cx="24" cy="23" r="12" fill="#FFFFFF" />
        <g fill="none" stroke={spec.ink} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          {spec.icon}
        </g>
      </svg>
      <i className="pp-shadow" />
    </span>
  );
}

const pct = (v: number, of: number) => `${((v / of) * 100).toFixed(3)}%`;

function pinStyle(s: PinSpec): CSSProperties {
  return {
    "--dx": pct(s.desk.x, DESK.w),
    "--dy": pct(s.desk.y, DESK.h),
    "--ds": `${s.desk.size}px`,
    "--dr": `${s.desk.rot}deg`,
    "--dw": `${s.w}px`,
    "--mx": pct(s.mob.x, MOB.w),
    "--my": pct(s.mob.y, MOB.h),
    "--ms": `${s.mob.size}px`,
    "--mr": `${s.mob.rot}deg`,
    "--mw": `${s.mw}px`,
    "--pin": s.color,
    "--pin-ink": s.ink,
  } as CSSProperties;
}

// ─── окно «Dərsliklər»: лист ожидания ───────────────────────────────────
function WaitlistDialog({ lang, open, onClose }: { lang: HomeLang; open: boolean; onClose: () => void }) {
  const c = PINS_COPY[lang].wait;
  const ref = useRef<HTMLDialogElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal?.();
      input.current?.focus();
    } else if (!open && d.open) d.close();
  }, [open]);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const phone = normalizePhoneAz(String(f.get("phone") ?? ""));
    if (!WAITLIST_PHONE_RE.test(phone)) return setErr(c.errPhone);
    setErr("");
    setBusy(true);
    try {
      const m = await import("@/lib/textbook-waitlist.functions");
      const r = await m.joinTextbookWaitlist({
        data: { phone, lang, utm_source: readUtm().utm_source, company: String(f.get("hp_x") ?? "") },
      });
      if (r.ok) {
        setDone(true);
        ev("textbook_waitlist_join", { lang });
      } else setErr(r.error === "phone" ? c.errPhone : r.error === "rate" ? c.errRate : c.errFail);
    } catch {
      setErr(c.errFail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <dialog
      ref={ref}
      className="pp-dialog"
      aria-labelledby="pp-wait-title"
      onClose={onClose}
      onClick={(e) => e.target === ref.current && onClose()}
    >
      <div className="pp-dialog__in">
        <button type="button" className="pp-dialog__x" aria-label={c.close} onClick={onClose}>
          ×
        </button>
        <span className="pp-dialog__pin" aria-hidden="true">
          <Drop spec={PINS.find((p) => p.id === "textbooks")!} />
        </span>
        <h2 id="pp-wait-title">{c.title}</h2>
        {done ? (
          <p className="pp-dialog__done" role="status">
            {c.done}
          </p>
        ) : (
          <form onSubmit={submit} noValidate>
            {/* Honeypot. Не «company»: такое поле браузер заполнял организацией человека (03.11.2026). */}
            <div className="hp-hp" aria-hidden="true">
              <label htmlFor="pp-wait-hp-x">Leave this empty</label>
              <input id="pp-wait-hp-x" type="text" name="hp_x" tabIndex={-1} autoComplete="off" data-1p-ignore="" data-lpignore="true" data-bwignore="" data-form-type="other" />
            </div>
            <label htmlFor="pp-wait-phone">{c.phoneLabel}</label>
            <span className="pp-phone">
              <span aria-hidden="true">+994</span>
              <input
                ref={input}
                id="pp-wait-phone"
                name="phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                placeholder={c.placeholder}
                maxLength={20}
                required
                aria-invalid={err === c.errPhone || undefined}
              />
            </span>
            {err && (
              <p className="hp-err" role="alert">
                {err}
              </p>
            )}
            <button type="submit" className="hp-btn" disabled={busy}>
              {busy ? c.sending : c.submit}
            </button>
          </form>
        )}
      </div>
    </dialog>
  );
}

// ─── первый экран ────────────────────────────────────────────────────────
export function HomePins({ lang, countries }: { lang: HomeLang; countries: number | null }) {
  const c = PINS_COPY[lang];
  const stage = useRef<HTMLDivElement>(null);
  const [waitOpen, setWaitOpen] = useState(false);

  // Пины слегка «подпрыгивают» по очереди; без движения — стоят на месте.
  // Только сдвиг, без прозрачности: сервер уже нарисовал пины видимыми.
  useEffect(() => {
    try {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
      const drops = stage.current?.querySelectorAll<HTMLElement>(".pp-drop");
      if (drops?.length) animate(drops, { y: [0, -10, 0] }, { duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: stagger(0.08, { startDelay: 0.15 }) });
    } catch {
      /* анимация — украшение */
    }
  }, []);

  const click = (pin: PinId) => ev("home_pin_click", { pin, lang });

  return (
    <section id="top" className="pp" aria-labelledby="pp-h1">
      <Streets />
      <div className="hp-wrap pp-in">
        {/* Слоган — крупно, как раньше, но не заголовок; H1 — со словами поиска
            на месте прежней строки под слоганом (29.10.2026). */}
        <p className="pp-h1">
          {c.slogan} <em className="hp-serif">{c.sloganTail}</em>
        </p>
        <h1 id="pp-h1" className="pp-sub">
          {c.h1}
        </h1>
        <p className="pp-ask">{c.ask}</p>

        <div className="pp-stage" ref={stage}>
          <Trails className="pp-trails pp-trails--desk" view={DESK} at={(p) => p.desk} />
          <Trails className="pp-trails pp-trails--mob" view={MOB} at={(p) => p.mob} />
          {PINS.map((s) => {
            const copy = s.id === "abroad" ? { ...c.pins.abroad, line: abroadPinLine(lang, countries) } : c.pins[s.id];
            const body = (
              <>
                <Drop spec={s} />
                <span className="pp-title">{copy.title}</span>
                <span className="pp-line">{copy.line}</span>
                <span className={`pp-tag pp-tag--${s.tag}`}>{copy.tag}</span>
              </>
            );
            return s.id === "textbooks" ? (
              <button
                key={s.id}
                type="button"
                className="pp-pin"
                data-pin={s.id}
                style={pinStyle(s)}
                aria-haspopup="dialog"
                onClick={() => {
                  click(s.id);
                  setWaitOpen(true);
                }}
              >
                {body}
              </button>
            ) : (
              <a
                key={s.id}
                className={`pp-pin${s.id === "career" ? " pp-pin--main" : ""}`}
                data-pin={s.id}
                data-cta={`pin_${s.id}`}
                href={pinHref(s.id, lang)}
                style={pinStyle(s)}
                onClick={() => click(s.id)}
              >
                {body}
              </a>
            );
          })}
        </div>

        {/* Ссылка «Yolutap nədir? ⌄» снята (27.10.2026): тот же заголовок — сразу под картой. */}
      </div>
      <WaitlistDialog lang={lang} open={waitOpen} onClose={() => setWaitOpen(false)} />
    </section>
  );
}
